// src/router.jsx

import { createBrowserRouter, redirect, Outlet, useRouteError } from 'react-router-dom';
import { lazy, Suspense, useEffect } from 'react';

import { isAuthenticated } from '../lib/guard.ts';
import { requireAuthAndActiveSubscription } from '../lib/guard.ts';
import { ScrollToTop } from '../components/ScrollToTop.jsx';
import { StoreLayout } from '../components/layout/StoreLayout.jsx'; 

import { LandingPage } from '../paginas/landing/LandingPage.jsx';
import { MarketplacePage } from '../paginas/landing/MarketplacePage.jsx';
import { Precios } from '../paginas/landing/Precios.jsx';
import { RegistroPage } from '../paginas/auth/registro/RegistroPage.jsx';
import { RegistroTiendaPage } from '../paginas/auth/registro/RegistroTiendaPage.jsx';
import { LoginPage } from '../paginas/auth/inicio_sesion/LoginPage.jsx';
import { VerificarCorreoPage } from '../paginas/auth/verificar_correo/VerificarCorreoPage.jsx';
import { RecuperarPasswordPage } from '../paginas/auth/recuperar_password/RecuperarPasswordPage.jsx';
import { HomeTienda } from '../paginas/tienda_publica/HomeTienda.jsx';
import { DetalleProducto } from '../paginas/tienda_publica/DetalleProducto.jsx';
import { Carrito } from '../paginas/tienda_publica/Carrito.jsx';
import { Checkout } from '../paginas/tienda_publica/CheckoutMultiStep.jsx';
import { OrdenResumen } from '../paginas/tienda_publica/orden_resumen/OrdenResumen.jsx';
import TerminosClientes from '../paginas/tienda_publica/TerminosClientes.jsx';
import PanelLayout from '../paginas/panel_tienda/PanelLayout.jsx';

// --- MANEJADOR DE ERRORES AUTOMÁTICO (FIX DEPLOY) ---
function GlobalErrorElement() {
  const error = useRouteError();

  useEffect(() => {
    // Detectamos si el error es por falta de archivos (Deploy nuevo)
    if (error?.message?.includes('dynamically imported module') || 
        error?.message?.includes('Importing a module script failed')) {
      
      const storageKey = 'vite-reload-on-chunk-error';
      const lastReload = sessionStorage.getItem(storageKey);
      const now = Date.now();

      // Recargamos solo si no hemos recargado en los últimos 10 segundos
      // Esto evita bucles infinitos si el error es persistente
      if (!lastReload || now - parseInt(lastReload) > 10000) {
        sessionStorage.setItem(storageKey, now.toString());
        window.location.reload();
      }
    }
  }, [error]);

  // UI de Fallback Visual (Estilo Luxury Space)
  return (
    <div className="min-h-screen bg-[#020202] text-white flex flex-col items-center justify-center p-4">
      <div className="w-16 h-16 rounded-full border-2 border-indigo-500/30 border-t-indigo-500 animate-spin mb-6"></div>
      <h2 className="text-xl font-bold mb-2">Actualizando sistema...</h2>
      <p className="text-white/40 text-sm">Detectamos una nueva versión. Recargando en unos segundos.</p>
      
      {/* Botón manual por si acaso */}
      <button 
        onClick={() => window.location.reload()}
        className="mt-8 px-6 py-2 rounded-full bg-white/10 hover:bg-white/20 text-xs font-bold uppercase tracking-widest transition-colors"
      >
        Recargar Ahora
      </button>
    </div>
  );
}

// Helper: acepta default o export nombrado
const lazyCompat = (factory, name) =>
  lazy(() => factory().then(m => ({ default: m.default ?? m[name] })));

// Lazy load heavy panel components (compatibles con default o nombrado)
const InicioPanel                 = lazyCompat(() => import('../paginas/panel_tienda/InicioPanel.jsx'),                                  'InicioPanel');
const ListaProductos              = lazyCompat(() => import('../paginas/panel_tienda/catalogo/ListaProductos.jsx'),                      'ListaProductos');
const CrearEditarProducto         = lazyCompat(() => import('../paginas/panel_tienda/catalogo/CrearEditarProducto.jsx'),                 'CrearEditarProducto');
const ImagenesProducto            = lazyCompat(() => import('../paginas/panel_tienda/catalogo/ImagenesProducto.jsx'),                   'ImagenesProducto');
const ListaPromociones            = lazyCompat(() => import('../paginas/panel_tienda/catalogo/ListaPromociones.jsx'),                  'ListaPromociones');
const CrearEditarPromocion        = lazyCompat(() => import('../paginas/panel_tienda/catalogo/CrearEditarPromocion.jsx'),               'CrearEditarPromocion');
const ListaPedidos                = lazyCompat(() => import('../paginas/panel_tienda/pedidos/ListaPedidos.jsx'),                         'ListaPedidos');
const DetallePedido               = lazyCompat(() => import('../paginas/panel_tienda/pedidos/DetallePedido.jsx'),                        'DetallePedido');
const ReportesPage                = lazyCompat(() => import('../paginas/panel_tienda/reportes/ReportesPage.jsx'),                        'ReportesPage');
const ReportesPromociones         = lazyCompat(() => import('../paginas/panel_tienda/reportes/ReportesPromociones.jsx'),                 'ReportesPromociones');
const ListaClientes               = lazyCompat(() => import('../paginas/panel_tienda/clientes/ListaClientes.jsx'),                       'ListaClientes');
const DetalleCliente              = lazyCompat(() => import('../paginas/panel_tienda/clientes/DetalleCliente.jsx'),                      'DetalleCliente');
const NotificacionesPage          = lazyCompat(() => import('../paginas/panel_tienda/notificaciones/NotificacionesPage.jsx'),            'NotificacionesPage');
const ConfiguracionNotificaciones = lazyCompat(() => import('../paginas/panel_tienda/notificaciones/ConfiguracionNotificaciones.jsx'),   'ConfiguracionNotificaciones');
const MetodosPagoConfig           = lazyCompat(() => import('../paginas/panel_tienda/pagos/MetodosPagoConfig.jsx'),                      'MetodosPagoConfig');
const AjustesTienda               = lazyCompat(() => import('../paginas/panel_tienda/ajustes/AjustesTienda.jsx'),                        'AjustesTienda');
const Suscripciones               = lazyCompat(() => import('../paginas/panel_tienda/Suscripciones.jsx'),                                 'Suscripciones');
const Ayuda                       = lazyCompat(() => import('../paginas/panel_tienda/Ayuda.jsx'),                                         'Ayuda');

// Lazy load super-admin components
const SuperAdminLayout            = lazyCompat(() => import('../paginas/super_admin/SuperAdminLayout.jsx'),                              'SuperAdminLayout');
const SuperAdminDashboard         = lazyCompat(() => import('../paginas/super_admin/SuperAdminDashboard.jsx'),                           'SuperAdminDashboard');
const SuperAdminUsuarios          = lazyCompat(() => import('../paginas/super_admin/SuperAdminUsuarios.jsx'),                            'SuperAdminUsuarios');
const SuperAdminDetalleUsuario    = lazyCompat(() => import('../paginas/super_admin/SuperAdminDetalleUsuario.jsx'),                      'SuperAdminDetalleUsuario');
const SuperAdminTiendas           = lazyCompat(() => import('../paginas/super_admin/SuperAdminTiendas.jsx'),                             'SuperAdminTiendas');
const SuperAdminDetalleTienda     = lazyCompat(() => import('../paginas/super_admin/SuperAdminDetalleTienda.jsx'),                       'SuperAdminDetalleTienda');
const SuperAdminFinanzas          = lazyCompat(() => import('../paginas/super_admin/SuperAdminFinanzas.jsx'),                            'SuperAdminFinanzas');
const SuperAdminCatalogo          = lazyCompat(() => import('../paginas/super_admin/SuperAdminCatalogo.jsx'),                            'SuperAdminCatalogo');
const SuperAdminPedidos           = lazyCompat(() => import('../paginas/super_admin/SuperAdminPedidos.jsx'),                             'SuperAdminPedidos');
const SuperAdminDetallePedido     = lazyCompat(() => import('../paginas/super_admin/SuperAdminDetallePedido.jsx'),                       'SuperAdminDetallePedido');
const SuperAdminClientes          = lazyCompat(() => import('../paginas/super_admin/SuperAdminClientes.jsx'),                            'SuperAdminClientes');
const SuperAdminPagosSuscripcion  = lazyCompat(() => import('../paginas/super_admin/SuperAdminPagosSuscripcion.jsx'),                    'SuperAdminPagosSuscripcion');
const SuperAdminDetallePagoSuscripcion = lazyCompat(() => import('../paginas/super_admin/SuperAdminDetallePagoSuscripcion.jsx'),         'SuperAdminDetallePagoSuscripcion');
const SuperAdminConfiguracion     = lazyCompat(() => import('../paginas/super_admin/SuperAdminConfiguracion.jsx'),                       'SuperAdminConfiguracion');
const SuperAdminTasasCambio       = lazyCompat(() => import('../paginas/super_admin/SuperAdminTasasCambio.jsx'),                         'SuperAdminTasasCambio');
const SuperAdminMonitoreo         = lazyCompat(() => import('../paginas/super_admin/SuperAdminMonitoreo.jsx'),                           'SuperAdminMonitoreo');
const SuperAdminNotificacionesPage = lazyCompat(() => import('../paginas/super_admin/notificaciones/SuperAdminNotificacionesPage.jsx'), 'SuperAdminNotificacionesPage');
const SuperAdminTestNotificaciones = lazyCompat(() => import('../paginas/super_admin/SuperAdminTestNotificaciones.jsx'),                'SuperAdminTestNotificaciones');
const SuperAdminFeedback           = lazyCompat(() => import('../paginas/super_admin/SuperAdminFeedback.jsx'),                          'SuperAdminFeedback');

const PerfilClientePage           = lazyCompat(() => import('../paginas/cliente/PerfilClientePage.jsx'),                                'PerfilClientePage');
const MainLayout                  = lazy(() => import('../components/layout/MainLayout.jsx'));

function requireAuthLoader() {
  if (!isAuthenticated()) {
    return redirect('/inicio-sesion');
  }
  return null;
}

function requireAuthAndSubscriptionLoader() {
  return requireAuthAndActiveSubscription((subscriptionStatus) => {
    return subscriptionStatus;
  });
}

function requireSuperAdminLoader() {
  if (!isAuthenticated()) {
    return redirect('/inicio-sesion');
  }
  return null;
}

// Layout para la parte pública (Landing, Auth, etc)
function RootLayout() {
  return (
    <>
      <ScrollToTop />
      <Outlet />
    </>
  );
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <GlobalErrorElement />, // <--- PROTECCIÓN AGREGADA
    children: [
      { index: true, element: <LandingPage /> },
      { path: 'marketplace', element: <MarketplacePage /> },
      { path: 'precios', element: <Precios /> },
      { path: 'registro', element: <RegistroPage /> },
      { path: 'registro/tienda', element: <RegistroTiendaPage /> },
      { path: 'inicio-sesion', element: <LoginPage /> },
      { path: 'verificar-correo', element: <VerificarCorreoPage /> },
      { path: 'recuperar-password', element: <RecuperarPasswordPage /> },
      { path: 'terminos-clientes', element: <TerminosClientes /> },
      {
        path: 't/:slug',
        element: <StoreLayout />, 
        children: [
          { index: true, element: <HomeTienda /> },
          { path: 'producto/:productoId', element: <DetalleProducto /> },
          { path: 'carrito', element: <Carrito /> },
          { path: 'checkout', element: <Checkout /> },
          { path: 'orden/:ordenId', element: <OrdenResumen /> },
        ],
      },
    ],
  },
  {
    path: '/panel',
    element: (
      <>
        <ScrollToTop />
        <PanelLayout />
      </>
    ),
    errorElement: <GlobalErrorElement />, // <--- PROTECCIÓN AGREGADA
    loader: requireAuthAndSubscriptionLoader,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Cargando...</div>}>
            <InicioPanel />
          </Suspense>
        ),
      },
      {
        path: 'catalogo',
        element: (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Cargando...</div>}>
            <ListaProductos />
          </Suspense>
        ),
      },
      {
        path: 'catalogo/nuevo',
        element: (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Cargando...</div>}>
            <CrearEditarProducto />
          </Suspense>
        ),
      },
      {
        path: 'catalogo/:productoId',
        element: (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Cargando...</div>}>
            <CrearEditarProducto />
          </Suspense>
        ),
      },
      {
        path: 'catalogo/:productoId/imagenes',
        element: (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Cargando...</div>}>
            <ImagenesProducto />
          </Suspense>
        ),
      },
      {
        path: 'promociones',
        element: (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Cargando...</div>}>
            <ListaPromociones />
          </Suspense>
        ),
      },
      {
        path: 'promociones/nueva',
        element: (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Cargando...</div>}>
            <CrearEditarPromocion />
          </Suspense>
        ),
      },
      {
        path: 'promociones/:promocionId',
        element: (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Cargando...</div>}>
            <CrearEditarPromocion />
          </Suspense>
        ),
      },
      {
        path: 'pedidos',
        element: (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Cargando...</div>}>
            <ListaPedidos />
          </Suspense>
        ),
      },
      {
        path: 'pedidos/:pedidoId',
        element: (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Cargando...</div>}>
            <DetallePedido />
          </Suspense>
        ),
      },
      {
        path: 'reportes',
        element: (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Cargando...</div>}>
            <ReportesPage />
          </Suspense>
        ),
      },
      {
        path: 'reportes/promociones',
        element: (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Cargando...</div>}>
            <ReportesPromociones />
          </Suspense>
        ),
      },
      {
        path: 'clientes',
        element: (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Cargando...</div>}>
            <ListaClientes />
          </Suspense>
        ),
      },
      {
        path: 'clientes/:clienteId',
        element: (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Cargando...</div>}>
            <DetalleCliente />
          </Suspense>
        ),
      },
      {
        path: 'notificaciones',
        element: (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Cargando...</div>}>
            <NotificacionesPage />
          </Suspense>
        ),
      },
      {
        path: 'configuracion/notificaciones',
        element: (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Cargando...</div>}>
            <ConfiguracionNotificaciones />
          </Suspense>
        ),
      },
      {
        path: 'pagos',
        element: (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Cargando...</div>}>
            <MetodosPagoConfig />
          </Suspense>
        ),
      },
      {
        path: 'suscripciones',
        element: (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Cargando...</div>}>
            <Suscripciones />
          </Suspense>
        ),
      },
      {
        path: 'ajustes',
        element: (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Cargando...</div>}>
            <AjustesTienda />
          </Suspense>
        ),
      },
      {
        path: 'ayuda',
        element: (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Cargando...</div>}>
            <Ayuda />
          </Suspense>
        ),
      },
    ]
  },
  {
    path: '/super-admin',
    element: (
      <>
        <ScrollToTop />
        <SuperAdminLayout />
      </>
    ),
    errorElement: <GlobalErrorElement />, // <--- PROTECCIÓN AGREGADA
    loader: requireSuperAdminLoader,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Cargando...</div>}>
            <SuperAdminDashboard />
          </Suspense>
        ),
      },
      {
        path: 'usuarios',
        element: (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Cargando...</div>}>
            <SuperAdminUsuarios />
          </Suspense>
        ),
      },
      {
        path: 'usuarios/:id',
        element: (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Cargando...</div>}>
            <SuperAdminDetalleUsuario />
          </Suspense>
        ),
      },
      {
        path: 'tiendas',
        element: (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Cargando...</div>}>
            <SuperAdminTiendas />
          </Suspense>
        ),
      },
      {
        path: 'tiendas/:tiendaId',
        element: (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Cargando...</div>}>
            <SuperAdminDetalleTienda />
          </Suspense>
        ),
      },
      {
        path: 'finanzas',
        element: (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Cargando...</div>}>
            <SuperAdminFinanzas />
          </Suspense>
        ),
      },
      {
        path: 'catalogo',
        element: (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Cargando...</div>}>
            <SuperAdminCatalogo />
          </Suspense>
        ),
      },
      {
        path: 'pedidos',
        element: (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Cargando...</div>}>
            <SuperAdminPedidos />
          </Suspense>
        ),
      },
      {
        path: 'pedidos/:pedidoId',
        element: (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Cargando...</div>}>
            <SuperAdminDetallePedido />
          </Suspense>
        ),
      },
      {
        path: 'clientes',
        element: (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Cargando...</div>}>
            <SuperAdminClientes />
          </Suspense>
        ),
      },
      {
        path: 'pagos-suscripcion',
        element: (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Cargando...</div>}>
            <SuperAdminPagosSuscripcion />
          </Suspense>
        ),
      },
      {
        path: 'pagos-suscripcion/:pagoId',
        element: (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Cargando...</div>}>
            <SuperAdminDetallePagoSuscripcion />
          </Suspense>
        ),
      },
      {
        path: 'configuracion',
        element: (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Cargando...</div>}>
            <SuperAdminConfiguracion />
          </Suspense>
        ),
      },
      {
        path: 'tasas-cambio',
        element: (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Cargando...</div>}>
            <SuperAdminTasasCambio />
          </Suspense>
        ),
      },
      {
        path: 'monitoreo',
        element: (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Cargando...</div>}>
            <SuperAdminMonitoreo />
          </Suspense>
        ),
      },
      {
        path: 'notificaciones',
        element: (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Cargando...</div>}>
            <SuperAdminNotificacionesPage />
          </Suspense>
        ),
      },
      {
        path: 'test-notificaciones',
        element: (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Cargando...</div>}>
            <SuperAdminTestNotificaciones />
          </Suspense>
        ),
      },
      {
        path: 'feedback',
        element: (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Cargando...</div>}>
            <SuperAdminFeedback />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: '/mi-cuenta',
    element: (
      <Suspense fallback={<div className="flex items-center justify-center h-screen">Cargando...</div>}>
        <ScrollToTop />
        <MainLayout /> 
      </Suspense>
    ),
    errorElement: <GlobalErrorElement />, // <--- PROTECCIÓN AGREGADA
    loader: requireAuthLoader,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Cargando perfil...</div>}>
            <PerfilClientePage />
          </Suspense>
        ),
      },
    ],
  },
]);